import { api } from './api';

export type EventType = 'call' | 'meeting' | 'task' | 'email' | 'sms' | 'note';
export type CallDirection = 'inbound' | 'outbound';
export type MeetingType = 'physical' | 'video';
export type TaskStatus = 'todo' | 'in_progress' | 'done';

export interface EventItem {
  id: string;
  type: EventType;
  prospectId: string;
  opportunityId: string | null;
  contactId: string | null;
  title: string | null;
  description: string | null;
  scheduledAt: string | null;
  durationMinutes: number | null;
  callDirection: CallDirection | null;
  callDurationMinutes: number | null;
  meetingType: MeetingType | null;
  meetingLocation: string | null;
  taskStatus: TaskStatus | null;
  taskDueDate: string | null;
  taskAssignedTo: string | null;
  createdBy: string;
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
  prospect: {
    id: string;
    type: 'company' | 'individual';
    companyName: string | null;
    firstName: string | null;
    lastName: string | null;
    status: string;
  };
  opportunity: { id: string; reference: string; stage: string } | null;
  contact: { id: string; firstName: string; lastName: string } | null;
  creator: { id: string; firstName: string; lastName: string; email: string };
  assignee: { id: string; firstName: string; lastName: string; email: string } | null;
  completer: { id: string; firstName: string; lastName: string; email: string } | null;
  // Provenance séquence (V1) - présent uniquement si l'événement a été généré
  // par une inscription dans une séquence. Cf SPECIFICATIONS §7.1.
  prospectSequence: {
    id: string;
    status: 'active' | 'completed' | 'cancelled';
    sequence: { id: string; name: string };
  } | null;
  sequenceStep: { id: string; stepOrder: number; title: string } | null;
}

export interface EventFilters {
  cursor?: string;
  limit?: number;
  type?: EventType;
  prospect_id?: string;
  opportunity_id?: string;
  task_assigned_to?: string;
  task_status?: TaskStatus;
  from?: string;
  to?: string;
}

export interface CreateEventPayload {
  type: EventType;
  prospectId: string;
  opportunityId?: string;
  contactId?: string;
  title?: string;
  description?: string;
  scheduledAt?: string;
  durationMinutes?: number;
  callDirection?: CallDirection;
  callDurationMinutes?: number;
  meetingType?: MeetingType;
  meetingLocation?: string;
  taskStatus?: TaskStatus;
  taskDueDate?: string;
  taskAssignedTo?: string;
}

// `opportunityId` peut valoir `null` en update pour DÉLIER un RDV de son
// opportunité (décision produit 2026-06-05). Les autres champs restent un
// Partial du payload de création.
export type UpdateEventPayload = Partial<
  Omit<CreateEventPayload, 'type' | 'prospectId' | 'opportunityId'>
> & {
  opportunityId?: string | null;
};

export interface CalendarRange {
  from: string;
  to: string;
  team_id?: string;
}

export interface UserCalendarResponse {
  target: { id: string; firstName: string; lastName: string };
  readonly: boolean;
  events: EventItem[];
}

export interface TeamCalendarResponse {
  teamId: string | null;
  events: EventItem[];
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function auth(token: string) {
  return { accessToken: token };
}

export const eventsApi = {
  list: (token: string, filters: EventFilters = {}) =>
    api.get<{ data: EventItem[]; nextCursor: string | null }>(
      `/api/v1/events${qs(filters as Record<string, unknown>)}`,
      auth(token),
    ),
  create: (token: string, body: CreateEventPayload) =>
    api.post<EventItem>('/api/v1/events', body, auth(token)),
  update: (token: string, id: string, body: UpdateEventPayload) =>
    api.patch<EventItem>(`/api/v1/events/${id}`, body, auth(token)),
  complete: (token: string, id: string) =>
    api.patch<EventItem>(`/api/v1/events/${id}/complete`, {}, auth(token)),
  delete: (token: string, id: string) => api.delete<void>(`/api/v1/events/${id}`, auth(token)),

  myCalendar: (token: string, range: CalendarRange) =>
    api.get<EventItem[]>(
      `/api/v1/events/calendar${qs(range as unknown as Record<string, unknown>)}`,
      auth(token),
    ),
  userCalendar: (token: string, userId: string, range: CalendarRange) =>
    api.get<UserCalendarResponse>(
      `/api/v1/events/calendar/${userId}${qs(range as unknown as Record<string, unknown>)}`,
      auth(token),
    ),
  teamCalendar: (token: string, range: CalendarRange) =>
    api.get<TeamCalendarResponse>(
      `/api/v1/events/calendar/team${qs(range as unknown as Record<string, unknown>)}`,
      auth(token),
    ),
  overdueTasks: (
    token: string,
    params: { scope?: 'mine' | 'team' | 'users'; user_ids?: string[] } = {},
  ) => {
    const q: Record<string, unknown> = {};
    if (params.scope) q.scope = params.scope;
    if (params.user_ids && params.user_ids.length > 0) q.user_ids = params.user_ids.join(',');
    return api.get<EventItem[]>(`/api/v1/events/tasks/overdue${qs(q)}`, auth(token));
  },
  /** Refonte 2026-05-22 : RDV passés non-done. Pattern miroir overdueTasks.
   *  Sémantique « RDV à moi » élargie (créés par moi OU sur un prospect qui
   *  m'est assigné), cohérente avec getUserAgenda et getMyCalendar. */
  overdueMeetings: (
    token: string,
    params: { scope?: 'mine' | 'team' | 'users'; user_ids?: string[] } = {},
  ) => {
    const q: Record<string, unknown> = {};
    if (params.scope) q.scope = params.scope;
    if (params.user_ids && params.user_ids.length > 0) q.user_ids = params.user_ids.join(',');
    return api.get<EventItem[]>(`/api/v1/events/meetings/overdue${qs(q)}`, auth(token));
  },
  agenda: (token: string) =>
    api.get<{ items: EventItem[]; total: number }>('/api/v1/events/agenda', auth(token)),
  activity: (
    token: string,
    filters: {
      cursor?: string;
      limit?: number;
      user_ids?: string[];
      types?: EventType[];
      from?: string;
      to?: string;
      prospect_id?: string;
    } = {},
  ) => {
    const q: Record<string, unknown> = {};
    if (filters.cursor) q.cursor = filters.cursor;
    if (filters.limit) q.limit = filters.limit;
    if (filters.user_ids && filters.user_ids.length > 0) q.user_ids = filters.user_ids.join(',');
    if (filters.types && filters.types.length > 0) q.types = filters.types.join(',');
    if (filters.from) q.from = filters.from;
    if (filters.to) q.to = filters.to;
    if (filters.prospect_id) q.prospect_id = filters.prospect_id;
    return api.get<{ data: EventItem[]; nextCursor: string | null }>(
      `/api/v1/events/activity${qs(q)}`,
      auth(token),
    );
  },
};

// -------- Labels & icônes --------------------------------------------------

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  call: 'Appel',
  meeting: 'RDV',
  task: 'Tâche',
  email: 'Email',
  sms: 'SMS',
  note: 'Note',
};

/**
 * Palette de tons par type d'event - V1 :
 *  - `meeting` → accent (couleur primaire de marque)
 *  - `task` → danger (rouge) pour les tâches **non-faites** ; les tâches done
 *    sont recolorées en `success` côté UI (cf `eventDotTone` dans les
 *    composants Timeline/Calendar/Activity).
 *  - `call` → purple (violet Stripe historique #635BFF)
 *  - `note` / `email` / `sms` → neutral
 */
export const EVENT_TYPE_TONES: Record<
  EventType,
  'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'purple'
> = {
  call: 'purple',
  meeting: 'accent',
  task: 'danger',
  email: 'neutral',
  sms: 'neutral',
  note: 'neutral',
};

/**
 * Helper pour le rendu dynamique des entités task-like :
 *  - tâches `done` → success (vert)
 *  - meetings `done` → success aussi (refonte 2026-05-21, Q3 : meetings
 *    task-like) — distingue visuellement un RDV effectué d'un RDV à venir
 *  - **meetings passés non-done → warning (orange)** — refonte 2026-05-22.
 *    Décision produit : RDV en retard distinct visuellement d'un RDV à venir
 *    sans pour autant prendre la criticité rouge des tâches en retard.
 *  - les autres types gardent leur tone statique de `EVENT_TYPE_TONES`.
 *    (NB. les tâches en retard ne sont PAS recolorées ici — elles restent
 *    sur le tone `danger` par défaut + le composant Timeline/Bell ajoute un
 *    badge texte « En retard » pour les tasks. C'est la convention historique.)
 */
export function eventDotTone(
  e: EventItem,
): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'purple' {
  if ((e.type === 'task' || e.type === 'meeting') && e.taskStatus === 'done') return 'success';
  // Meeting passé non-done → orange (warning). isTaskOverdue détecte déjà ce
  // cas pour les meetings depuis la refonte 2026-05-21 (meeting task-like).
  if (e.type === 'meeting' && isTaskOverdue(e)) return 'warning';
  return EVENT_TYPE_TONES[e.type];
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'À faire',
  in_progress: 'En cours',
  done: 'Fait',
};

/**
 * Vrai si l'entité task-like (task OU meeting depuis refonte 2026-05-21) est
 * en retard : non terminée et échéance dépassée. Les meetings utilisent
 * `scheduledAt` comme échéance (pas `taskDueDate`), les tasks utilisent
 * `taskDueDate`. Les autres types ne sont pas concernés.
 */
export function isTaskOverdue(e: EventItem): boolean {
  if (e.type === 'task') {
    if (e.taskStatus === 'done') return false;
    if (!e.taskDueDate) return false;
    return new Date(e.taskDueDate).getTime() < Date.now();
  }
  if (e.type === 'meeting') {
    if (e.taskStatus === 'done') return false;
    if (!e.scheduledAt) return false;
    return new Date(e.scheduledAt).getTime() < Date.now();
  }
  return false;
}
